import { useEffect, useState, useMemo } from "react";
import styles from "./index.module.less";
import { useLocation, useNavigate } from "react-router-dom";
import { Iconsvg, ButtonField } from "@wk/components-react16";
import Icon from "@/components/Icon";
import {
  getHistoryDetail,
  getCaseList,
  getKsDict,
  getDictIdByCode,
  getDictListById,
} from "../../../services/history";

import { findValueByIdAndKey } from "@/utils";
import noImg from "@/assets/img/noImg.png";
import config from "@/config/env";

// 简单的消息提示组件
const showMessage = (message, type = "info") => {
  // 使用原生alert进行简单提示，或者可以考虑使用更美观的toast库
  alert(message);
};

const HistoryDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentId, setCurrentId] = useState(null);
  const [detail, setDetail] = useState({});
  const [moreList, setMoreList] = useState([]);
  const [selectedTag, setSelectedTag] = useState(""); // 当前选择的标签
  const [showTooltip, setShowTooltip] = useState(false); // 控制复制链接提示框
  const [showQRCode, setShowQRCode] = useState(false); // 控制二维码显示
  const [qrCodeType, setQRCodeType] = useState(""); // 二维码类型：weixin或pengyoujuan
  const [ksList, setKsList] = useState([]);
  const [alList, setAlList] = useState([]);
  const [yyList, setYyList] = useState([]);

  // 加载评委详情数据
  const loadJudgeDetail = (id) => {
    // 更新浏览器URL，但不刷新页面
    window.history.replaceState(null, "", `/history/detail?id=${id}`);
    setCurrentId(id);

    // 获取评委详情
    getHistoryDetail(id).then((res) => {
      if (res.code === 200) {
        const resData = res.result;
        resData.imgUrlList = resData.imgUrl
          ? resData.imgUrl.split(",").slice(0, 2)
          : [noImg];
        setDetail(resData);
      }
    });

    // 获取其他评委列表
    getCaseList({
      pageNo: 1,
      pageSize: 10,
      isAward: "1",
      px: "1",
      onlineStatus: "2",
    }).then((res) => {
      if (res.code === 200) {
        let filterResult = res.result.records.filter((item) => item.id !== id);
        // filterResult数组随机截取4个
        if (filterResult && filterResult.length > 4) {
          filterResult = filterResult
            .sort(() => 0.5 - Math.random())
            .slice(0, 4);
        }
        setMoreList(filterResult);
      }
    });
  };

  // 点击其他评委时，直接调用loadJudgeDetail加载数据，不再使用navigate
  const goDetail = (id) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    // 判断是否为当前评委ID，如果是，则不处理
    if (id === currentId) {
      return;
    }
    loadJudgeDetail(id);
  };

  // 获取当前页面URL
  const getCurrentPageUrl = () => {
    return window.location.href;
  };

  // 复制文本到剪贴板
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setShowTooltip(true);
        setTimeout(() => setShowTooltip(false), 2000); // 2秒后隐藏提示
      },
      (err) => {
        console.error("无法复制文本: ", err);
        showMessage("复制失败，请重试", "error");
      }
    );
  };

  const copyLink = (text) => {
    let textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 2000); // 2秒后隐藏提示
  };

  // 分享到微信
  const shareToWeChat = () => {
    setQRCodeType("weixin");
    setShowQRCode(true);
  };

  // 分享到朋友圈
  const shareToWeChatMoments = () => {
    setQRCodeType("pengyoujuan");
    setShowQRCode(true);
  };

  // 关闭二维码
  const closeQRCode = () => {
    setShowQRCode(false);
    setQRCodeType("");
  };

  // 分享到微博
  const shareToWeibo = () => {
    // 微博分享API
    const weiboShareUrl = `http://service.weibo.com/share/share.php?url=${encodeURIComponent(
      getCurrentPageUrl()
    )}&title=${encodeURIComponent(
      detail.name || "UpToDate案例"
    )}&pic=${encodeURIComponent(detail.headImg || "")}`;
    window.open(weiboShareUrl, "_blank");
  };
  const getCaseTypeDict = async (code) => {
    try {
      const resList = await getDictIdByCode({ code });
      const id = resList?.result?.records[0]?.id || "";
      const itemList = await getDictListById({ dictId: id, pageSize: -1 });
      return itemList?.result?.records || [];
    } catch (error) {
      return [];
    }
  };

  const getAlList = async () => {
    const zcList = await getCaseTypeDict("al");
    setAlList(zcList);
  };

  const getYyList = async () => {
    const zcList = await getCaseTypeDict("yy");
    setYyList(zcList);
  };

  const ksText = useMemo(() => {
    const value = detail?.dept;
    if (!value) {
      return detail.keshi || "";
    }

    return value
      .split(",")
      .map((item) => findValueByIdAndKey(ksList, item, "title", "key"))
      .join("、");
  }, [ksList, detail]);

  const alText = useMemo(() => {
    return (
      findValueByIdAndKey(alList, detail.caseType, "itemText", "id") ||
      detail.anliType ||
      ""
    );
  }, [alList, detail]);

  const nameText = useMemo(() => {
    let textArray = [
      detail?.userName || "",
      detail?.firstName || "",
      detail?.secondName,
    ].filter((item) => item);

    return textArray.join("、") || "";
  }, [detail]);

  const yyText = useMemo(() => {
    return (
      findValueByIdAndKey(yyList, detail.institution, "itemText") ||
      detail.hospital ||
      ""
    );
  }, [yyList, detail]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get("id"); // 获取查询参数 id
    if (id) {
      loadJudgeDetail(id);
    }
    getKsDict().then((res) => {
      setKsList(res.result || []);
    });
    getAlList();
    getYyList();
  }, []);

  return (
    <div className={styles["history-detail"]}>
      {/* 微信分享二维码弹窗 */}
      {showQRCode && (
        <div className="qrcode-modal">
          <div className="qrcode-container">
            <div className="qrcode-close" onClick={closeQRCode}>
              ×
            </div>
            <div className="qrcode-title">
              {qrCodeType === "weixin"
                ? "微信扫一扫，分享"
                : "朋友圈扫一扫，分享"}
            </div>
            <div className="qrcode-image">
              {/* 使用当前页面URL生成QR Code */}
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
                  window.location.href
                )}`}
                alt="分享二维码"
              />
            </div>
            <div className="qrcode-tips">
              {qrCodeType === "weixin"
                ? "打开微信，点击底部的'发现'，使用'扫一扫'即可将网页分享给好友"
                : "打开微信，点击底部的'发现'，使用'扫一扫'后点击右上角分享到朋友圈"}
            </div>
          </div>
        </div>
      )}

      {/* 顶部导航栏 */}
      {/* <div className="header-navigation">
        <div className="back-button" onClick={() => navigate(-1)}>
          <Iconsvg name="arrow-left" size={16} />
          <span>返回书籍山选</span>
        </div>
      </div> */}

      {/* 标题和分享图标 */}
      <div className="title-share-container">
        <div className="history-detail-title">作品详情</div>
        <div className="header-share-icons">
          <div onClick={shareToWeChatMoments}>
            <Icon icon="pengyoujuan" style={{ fontSize: "26px" }} />
          </div>
          <div onClick={shareToWeChat}>
            <Icon icon="weixin" style={{ fontSize: "26px" }} />
          </div>
          <div onClick={shareToWeibo}>
            <Icon icon="weibo" style={{ fontSize: "26px" }} />
          </div>
          {/* <div onClick={() => copyToClipboard(getCurrentPageUrl())}> */}
          <div onClick={() => copyLink(getCurrentPageUrl())}>
            <Icon icon="lianxie" style={{ fontSize: "26px" }} />
            {showTooltip && (
              <div className="copy-success-tooltip">链接已复制</div>
            )}
          </div>
        </div>
      </div>

      {/* 标签列表 */}
      <div className="tags-container">
        {detail.isAward == "1" && <div className="tag-item">获奖案例</div>}
        {ksText && <div className="tag-item">{ksText}</div>}
        {detail.belongYear && (
          <div className="tag-item">{`${detail.belongYear}年`}</div>
        )}
        {alText && <div className="tag-item">{alText}</div>}
      </div>

      {/* 案例标题 */}
      <div className="case-title">{detail.name}</div>

      {/* 参赛编号 */}
      <div className="case-number">参赛编号：{detail.number}</div>

      <div className="history-detail-content">
        <div className="history-detail-content-left">
          {detail.imgUrlList &&
            detail.imgUrlList.length > 0 &&
            detail.imgUrlList.map((item, index) => {
              return (
                <img
                  src={item}
                  key={index}
                  alt=""
                  style={{
                    height: detail.imgUrlList.length > 1 ? "50%" : "100%",
                  }}
                />
              );
            })}
        </div>

        <div className="history-detail-content-right">
          <div className="fourth-box">{detail.description}</div>
          <div className="author-card">
            <div className="author-name">{nameText}</div>
            <div className="author-title">{`${yyText}  ${ksText}`}</div>
          </div>
          <div
            className="preview-button"
            onClick={() => {
              if (!detail?.fileUrl) {
                return;
              }

              // 判断detail.fileUrl是http时，替换为https
              if (detail.fileUrl.startsWith("http://")) {
                detail.fileUrl = detail.fileUrl.replace("http://", "https://");
              }

              // 如果是pdf文件
              if (detail.fileUrl && detail.fileUrl.endsWith(".pdf")) {
                window.open(
                  `${config.pdfBaseUrl}/viewer.html?file=${detail.fileUrl}`,
                  "_blank"
                );
              } else {
                // 如果是其他文件
                window.open(detail.fileUrl, "_blank");
              }
            }}
          >
            <div className="button-text">作品预览</div>
            <Iconsvg name="arrow-right" size={16} color="#007AC3"></Iconsvg>
          </div>
        </div>
      </div>

      <div className="history-detail-more">
        <div className="history-detail-more-title">更多案例</div>

        <div className="history-detail-more-list">
          {moreList.map((item) => {
            return (
              <div key={item.id} onClick={() => goDetail(item.id)}>
                <div className="history-detail-more-list-item">
                  <div className="title">{item.name}</div>
                  <div className="desc">{item.description}</div>
                </div>
                <ButtonField iconPosition={"right"} mode={"ghost"}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation(); // 防止事件冒泡
                      goDetail(item.id);
                    }}
                  >
                    查看
                    <Iconsvg name="arrow-right" size={16}></Iconsvg>
                  </button>
                </ButtonField>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
export default HistoryDetail;
